import { useMemo, useEffect, useCallback, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSupportCategories, useSupportArticles } from 'domains/support-section/hooks';
import { useTrack } from 'utils/hooks/useMixPanel';
import { SUPPORT_MIXPANEL_EVENTS } from '../mixpanel';
import { normalizeArticles } from './normalizeArticles';

export const INITIAL_STATE = {
  articlesWithoutSubCategory: [],
  articlesWithSubCategory: [],
};

export const useSupportCategoryPage = () => {
  const { isLoading: isCategoriesLoading, data: categories } = useSupportCategories();
  const { isLoading: isArticlesLoading, data: articles } = useSupportArticles();
  const { categoryName = '' } = useParams();

  const { track } = useTrack();

  const [searchParams, setSearchParams] = useSearchParams();
  const [topic, setTopic] = useState('');
  const article = searchParams.get('article');

  const isLoading = useMemo(() => {
    return isCategoriesLoading || isArticlesLoading;
  }, [isCategoriesLoading, isArticlesLoading]);

  const selectedCategory = useMemo(() => {
    if (!categories || isLoading) return undefined;
    return categories.find(category => category.fields.categoryTitle.toLowerCase() === categoryName.toLowerCase());
  }, [categories, categoryName, isLoading]);

  const handleSelectedElement = useCallback(
    (id: string) => {
      track(SUPPORT_MIXPANEL_EVENTS.clicked.subcategory, {
        category: categoryName,
        subcategory: id,
      });
    },
    [categoryName, track],
  );

  useEffect(() => {
    if (topic) {
      const element = document.getElementById(topic);
      if (element) {
        handleSelectedElement(topic);
      }
    }
  }, [handleSelectedElement, topic]);

  const handleSubCategoryClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      handleSelectedElement(id);
      setSearchParams({ ...searchParams });
      setTopic(id);
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const categoryArticles = useMemo(() => {
    if (!articles || !categories || isLoading) return INITIAL_STATE;

    return isLoading ? INITIAL_STATE : normalizeArticles(articles, categories)[categoryName];
  }, [articles, categories, categoryName, isLoading]);

  return {
    isLoading,
    categories,
    articles,
    article,
    categoryName,
    selectedCategory,
    categoryArticles,
    handlers: {
      onClickTopic: (_category: string, title: string, currentTopic: string) => {
        const element = document.getElementById(currentTopic);
        if (element) {
          // get if element has aria-expanded=true inner element
          const isExpanded = element.querySelector('[aria-expanded="true"]');
          if (!isExpanded) {
            element.scrollIntoView({ behavior: 'smooth' });
            setSearchParams({
              article: title,
            });
            setTopic(currentTopic);
            track(SUPPORT_MIXPANEL_EVENTS.clicked.article, {
              category: categoryName,
              article: title,
            });
          }
        }
      },
      handleSubCategoryClick,
    },
  };
};
